import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type {
  PaymentIntent,
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
} from "@stripe/stripe-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { RotatingLines } from "react-loader-spinner";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import oops from "../assests/tipperUiSection/paynow.png";
import { reviewLinksApi, sendTipApi } from "../store/slices/paymentSlices";
import { AppDispatch, RootState } from "../store/store";
import styles from "./stripePayment.module.scss";
import ThemeButton from "./theme/themeButton";
import ConfirmationModal from "./tipperUiSectionLayout/modal/ConfirmationModal";
import { toast } from "react-toastify";
import {
  clearPendingTipPayment,
  savePendingTipPayment,
} from "../utils/functions";

/** Merchant country code for Stripe Payment Request (Google Pay / Apple Pay). */
function countryCodeForCurrency(currency: string): string {
  const map: Record<string, string> = {
    usd: "US",
    cad: "CA",
    gbp: "GB",
    aud: "AU",
    nzd: "NZ",
    eur: "IE",
    jpy: "JP",
    mxn: "MX",
  };
  return map[currency.toLowerCase()] || "US";
}

function validateTipEmail(value: string) {
  return /^(?:[^\s@]+@[^\s@]+\.[^\s@]+)?$/.test(value);
}

// "card"   → user picks card form
// "wallet" → user picks the device-native wallet (Apple Pay on iOS Safari,
//            Google Pay on Chrome/Android). Which one is shown is decided
//            at runtime by paymentRequest.canMakePayment().
type WalletTab = "card" | "wallet";
type AvailableWallet = "apple_pay" | "google_pay" | null;

export default function StripePayment() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [cardholderNameError, setCardholderNameError] = useState("");
  const [paymentElementError, setPaymentElementError] = useState("");
  const [stripeLoading, setStripeLoading] = useState(false);
  const [termsChecked, setTermsChecked] = useState(true);
  const [termsError, setTermsError] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [walletTab, setWalletTab] = useState<WalletTab>("card");
  // Which wallet did the device + Stripe Dashboard config actually expose?
  // - iPhone Safari (Apple Pay domain verified) → "apple_pay"
  // - Chrome / Android with Google Pay support  → "google_pay"
  // - Neither                                    → null (tab is hidden)
  const [availableWallet, setAvailableWallet] =
    useState<AvailableWallet>(null);
  const stripe = useStripe();
  const elements = useElements();
  const {
    sendTipLoading,
    postTipAmount,
    isAllThreeFeatures,
    property_service_id,
    showCardholderName,
    review_uuid,
  } = useSelector((store: RootState) => store.paymentSlices);
  const isEmailRequired = postTipAmount?.data?.is_tipper_email_required === 1;
  const { sessionId } = useSelector((store: RootState) => store.sessionSlice);
  const [errorModal, setErrorModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cardholderName, setCardholderName] = useState("");
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
    null
  );
  const handlersRef = useRef({
    termsChecked,
    emailTrim: "",
    cardholderTrim: "",
    postTipPayload: postTipAmount?.data,
    sessionId,
    showCardholderName,
    isAllThreeFeatures,
    property_service_id,
    isEmailRequired,
    availableWallet,
    review_uuid,
  });
  handlersRef.current = {
    termsChecked,
    emailTrim: email.trim(),
    cardholderTrim: cardholderName.trim(),
    postTipPayload: postTipAmount?.data,
    sessionId,
    showCardholderName,
    isAllThreeFeatures,
    property_service_id,
    isEmailRequired,
    availableWallet,
    review_uuid,
  };
  const handleErrorModal = () => setErrorModal(!errorModal);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isWalletPayment = walletTab === "wallet";
  const shouldCollectCardholderNameOutsideStripe = showCardholderName;
  const shouldRequireManualCardholderName =
    shouldCollectCardholderNameOutsideStripe && !isWalletPayment;
  // Display label used in error messages — driven by which wallet was
  // detected, not by a hardcoded "Google Pay".
  const walletDisplayLabel =
    availableWallet === "apple_pay"
      ? "Apple Pay"
      : availableWallet === "google_pay"
        ? "Google Pay"
        : "wallet";
  const selectedPaymentLabel = isWalletPayment
    ? walletDisplayLabel
    : selectedPaymentMethod === "apple_pay"
      ? "Apple Pay"
      : selectedPaymentMethod === "google_pay"
        ? "Google Pay"
        : "wallet";
  const isInlinePaymentValidationMessage = (message?: string) =>
    /fill in your details|fill in your card details|payment details|card details|complete your|card number is incomplete|card's expiry date is incomplete|card's security code is incomplete/i.test(
      message || ""
    );
  const getPaymentElementErrorMessage = (message?: string) => {
    const fallbackMessage =
      message || "Complete your payment details to continue.";
    if (!isWalletPayment && isInlinePaymentValidationMessage(message)) {
      return "";
    }
    if (!isWalletPayment) {
      return fallbackMessage;
    }
    if (
      !message ||
      /fill in your details|fill in your card details|payment details|card details|complete your/i.test(
        message
      )
    ) {
      return `Complete the ${selectedPaymentLabel} step to continue.`;
    }
    return message;
  };
  const finalizeTipAfterSucceededIntent = useCallback(
    async (
      paymentIntent: PaymentIntent,
      opts: {
        resolvedEmailFallback: string;
        manualCardholderName: string;
        treatLikeWalletBilling: boolean;
      }
    ): Promise<boolean> => {
      const ctx = handlersRef.current;
      const d = ctx.postTipPayload;
      if (!d) {
        toast.error("Payment data was lost. Please refresh and try again.");
        return false;
      }
      const confirmedPaymentMethod =
        paymentIntent.payment_method &&
          typeof paymentIntent.payment_method === "object"
          ? paymentIntent.payment_method
          : null;
      const confirmedBillingName =
        confirmedPaymentMethod?.billing_details?.name?.trim() ||
        paymentIntent.shipping?.name?.trim() ||
        opts.manualCardholderName.trim() ||
        "";
      const confirmedBillingEmail =
        opts.resolvedEmailFallback ||
        confirmedPaymentMethod?.billing_details?.email?.trim() ||
        paymentIntent.receipt_email?.trim() ||
        "";
      const payload: {
        application_fee: number | null;
        email: string | null;
        status: number;
        stripe_fee: number | null;
        stripe_transaction_id: string | null;
        tip_amount: number | null;
        type: string;
        uuid: string;
        note: typeof d.note;
        is_escrowed: typeof d.is_escrowed;
        customer_name?: string;
        property_service_id?: typeof ctx.property_service_id;
        review_uuid?: string | null;
      } = {
        application_fee:
          d.application_fee == null ? null : Number(d.application_fee),
        email: confirmedBillingEmail || null,
        status: 1,
        stripe_fee: d.stripe_fee == null ? null : Number(d.stripe_fee),
        stripe_transaction_id: paymentIntent?.id ?? null,
        tip_amount: d.tip_amount == null ? null : Number(d.tip_amount),
        type: String(d.type ?? ""),
        uuid: String(d.uuid ?? ""),
        note: d?.note,
        is_escrowed: d?.is_escrowed,
      };
      if (
        (ctx.showCardholderName || opts.treatLikeWalletBilling) &&
        confirmedBillingName
      ) {
        payload.customer_name = confirmedBillingName;
      }
      if (ctx.isAllThreeFeatures) {
        payload.property_service_id = ctx.property_service_id;
      }
      if (ctx.review_uuid) {
        payload.review_uuid = ctx.review_uuid;
      }
      try {
        const sendTipResponse = await dispatch(
          sendTipApi({
            data: payload,
            sessionId: ctx.sessionId,
          })
        ).unwrap();
        if (sendTipResponse?.status !== 1) {
          throw new Error(
            sendTipResponse?.message ||
            "Payment completed, but the tip could not be saved."
          );
        }
        clearPendingTipPayment();
        navigate("/payment-completed");
        return true;
      } catch (sendTipError: any) {
        toast.error(
          sendTipError?.message ||
          sendTipError?.error ||
          "Payment completed, but the tip could not be saved."
        );
        return false;
      }
    },
    [dispatch, navigate]
  );
  const handleSubmit = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
    setLoading(true);
    // reset all errors first
    setPaymentElementError("");
    setTermsError("");
    setError("");
    setCardholderNameError("");
    if (!stripe || !elements) {
      setPaymentElementError("Payment form is still loading. Please try again.");
      setLoading(false);
      return;
    }
    let hasValidationError = false;
    const manuallyEnteredCardholderName = cardholderName.trim();
    const resolvedCardholderName = shouldRequireManualCardholderName
      ? manuallyEnteredCardholderName
      : "";
    const resolvedEmail = email.trim();
    // ✅ Terms validation
    if (!termsChecked) {
      setTermsError("You must agree to the terms and conditions");
      hasValidationError = true;
    }
    // ✅ Email validation
    if (isEmailRequired && !resolvedEmail) {
      setError("Email is required");
      hasValidationError = true;
    } else if (resolvedEmail && !validateTipEmail(resolvedEmail)) {
      setError("Please enter a valid email address");
      hasValidationError = true;
    }
    // ✅ Stripe validation (ALWAYS run)
    const submitResult = await elements.submit();
    if (submitResult.error) {
      setPaymentElementError(
        getPaymentElementErrorMessage(submitResult.error.message)
      );
      hasValidationError = true;
    }
    // ✅ Cardholder validation
    if (shouldRequireManualCardholderName && !resolvedCardholderName) {
      setCardholderNameError("Please enter cardholder name");
      hasValidationError = true;
    }
    // ❗ Stop if any validation failed
    if (hasValidationError) {
      setLoading(false);
      return;
    }
    // ✅ Prepare billing details
    const billingDetails: { name?: string; email?: string } = {};
    if (resolvedCardholderName) {
      billingDetails.name = resolvedCardholderName;
    }
    if (resolvedEmail) {
      billingDetails.email = resolvedEmail;
    }
    const confirmParams: {
      return_url: string;
      payment_method_data?: {
        billing_details: {
          name?: string;
          email?: string;
        };
      };
    } = {
      return_url: process.env.REACT_APP_WEBSITE_URL + "payment-completed",
    };
    if (Object.keys(billingDetails).length > 0) {
      confirmParams.payment_method_data = {
        billing_details: billingDetails,
      };
    }
    const pendingTipPayload: any = {
      application_fee: postTipAmount.data.application_fee,
      email: resolvedEmail || null,
      status: 1,
      stripe_fee: postTipAmount.data.stripe_fee,
      stripe_transaction_id: null,
      tip_amount:
        postTipAmount.data.tip_amount == null
          ? null
          : Number(postTipAmount.data.tip_amount),
      type: postTipAmount.data.type,
      uuid: postTipAmount.data.uuid,
      note: postTipAmount?.data?.note,
      is_escrowed: postTipAmount?.data?.is_escrowed,
    };
    if (showCardholderName && resolvedCardholderName) {
      pendingTipPayload.customer_name = resolvedCardholderName;
    }
    if (isAllThreeFeatures) {
      pendingTipPayload.property_service_id = property_service_id;
    }
    if (review_uuid) {
      pendingTipPayload.review_uuid = review_uuid;
    }
    savePendingTipPayment({
      clientSecret: postTipAmount.data.clientSecret,
      sessionId,
      payload: pendingTipPayload,
    });
    // ✅ Confirm payment (card flow only — wallet flow uses paymentRequest.show())
    const result = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams,
    });
    // ❌ Handle Stripe errors
    if (result?.error) {
      if (result.error.type === "validation_error") {
        setPaymentElementError(
          getPaymentElementErrorMessage(result.error.message)
        );
      } else {
        toast.error(result.error.message);
        dispatch(
          reviewLinksApi({
            data: {
              send_tip_screen: true,
              error_reason: result.error.message,
            },
            sessionId,
          })
        );
      }
      clearPendingTipPayment();
      setLoading(false);
      return;
    }
    // ✅ Success flow
    if (result?.paymentIntent?.status === "succeeded") {
      const ok = await finalizeTipAfterSucceededIntent(result.paymentIntent, {
        resolvedEmailFallback: resolvedEmail,
        manualCardholderName: manuallyEnteredCardholderName,
        treatLikeWalletBilling: false,
      });
      if (!ok) {
        setLoading(false);
        return;
      }
    }
    setLoading(false);
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputEmail = e.target.value;
    setEmail(inputEmail);
    if (isEmailRequired && inputEmail.trim() === "") {
      setError("Email is required");
      return;
    }
    if (!validateTipEmail(inputEmail)) {
      setError("Please enter a valid email address");
    } else {
      setError("");
    }
  };
  // ✅ Wallet flow (Apple Pay on iOS Safari / Google Pay on Chrome+Android)
  // via Stripe PaymentRequest. canMakePayment() tells us which wallet is
  // actually available on this device — we render the tab branding for it.
  useEffect(() => {
    const clientSecret = postTipAmount?.data?.clientSecret;
    if (!stripe || !clientSecret) {
      setPaymentRequest(null);
      setAvailableWallet(null);
      return undefined;
    }
    let canceled = false;
    let activePr: PaymentRequest | null = null;
    const onPaymentMethod = async (ev: PaymentRequestPaymentMethodEvent) => {
      const stripeInst = stripe;
      const ctx = handlersRef.current;
      const tip = ctx.postTipPayload;
      const secret = tip?.clientSecret;
      const walletName =
        ctx.availableWallet === "apple_pay" ? "Apple Pay" : "Google Pay";
      if (!stripeInst || !tip || !secret) {
        ev.complete("fail");
        return;
      }
      if (!ctx.termsChecked) {
        toast.error("You must agree to the terms and conditions");
        ev.complete("fail");
        return;
      }
      const billingFromPm = ev.paymentMethod.billing_details;
      const payerEmail = (ev.payerEmail || billingFromPm?.email || "").trim();
      const payerName = (ev.payerName || billingFromPm?.name || "").trim();
      const resolvedWalletEmail = payerEmail || ctx.emailTrim;
      if (ctx.isEmailRequired && !resolvedWalletEmail.trim()) {
        toast.error("Email is required");
        ev.complete("fail");
        return;
      }
      if (resolvedWalletEmail && !validateTipEmail(resolvedWalletEmail)) {
        toast.error("Please enter a valid email address");
        ev.complete("fail");
        return;
      }
      if (ctx.showCardholderName && !payerName) {
        toast.error(
          `Please enter your name in ${walletName} to continue.`
        );
        ev.complete("fail");
        return;
      }
      setLoading(true);
      const resolvedEmail = resolvedWalletEmail || ctx.emailTrim;
      const resolvedCustomerName = payerName || ctx.cardholderTrim;
      const pendingTipPayload = {
        application_fee: tip.application_fee,
        email: resolvedEmail || null,
        status: 1,
        stripe_fee: tip.stripe_fee,
        stripe_transaction_id: null,
        tip_amount: tip.tip_amount == null ? null : Number(tip.tip_amount),
        type: tip.type,
        uuid: tip.uuid,
        note: tip?.note,
        is_escrowed: tip?.is_escrowed,
        ...(ctx.showCardholderName && resolvedCustomerName
          ? { customer_name: resolvedCustomerName }
          : {}),
        ...(ctx.isAllThreeFeatures
          ? { property_service_id: ctx.property_service_id }
          : {}),
        ...(ctx.review_uuid ? { review_uuid: ctx.review_uuid } : {}),
      };
      savePendingTipPayment({
        clientSecret: secret,
        sessionId: ctx.sessionId,
        payload: pendingTipPayload,
      });
      const { error: confirmError, paymentIntent } =
        await stripeInst.confirmCardPayment(secret, {
          payment_method: ev.paymentMethod.id,
          receipt_email: resolvedEmail || undefined,
        });
      if (confirmError) {
        ev.complete("fail");
        toast.error(confirmError.message);
        clearPendingTipPayment();
        dispatch(
          reviewLinksApi({
            data: {
              send_tip_screen: true,
              error_reason: confirmError.message,
            },
            sessionId: ctx.sessionId,
          })
        );
        setLoading(false);
        return;
      }
      ev.complete("success");
      if (paymentIntent?.status !== "succeeded") {
        clearPendingTipPayment();
        setLoading(false);
        return;
      }
      const finalized = await finalizeTipAfterSucceededIntent(paymentIntent, {
        resolvedEmailFallback: resolvedEmail,
        manualCardholderName: payerName || ctx.cardholderTrim,
        treatLikeWalletBilling: true,
      });
      if (!finalized) {
        clearPendingTipPayment();
      }
      setLoading(false);
    };
    void (async () => {
      const retrieve = await stripe.retrievePaymentIntent(clientSecret);
      if (canceled || retrieve.error || !retrieve.paymentIntent?.currency) {
        return;
      }
      const { paymentIntent } = retrieve;
      const pr = stripe.paymentRequest({
        country: countryCodeForCurrency(paymentIntent.currency),
        currency: paymentIntent.currency,
        total: {
          label: "Tip",
          amount: paymentIntent.amount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });
      // canMakePayment() returns null OR an object like
      //   { applePay: boolean, googlePay: boolean, link: boolean }
      // We use it to decide which wallet branding to show on the tab.
      const canPay = await pr.canMakePayment();
      if (canceled || !canPay) {
        setAvailableWallet(null);
        return;
      }
      let detected: AvailableWallet = null;
      if (canPay.applePay) {
        detected = "apple_pay";
      } else if (canPay.googlePay) {
        detected = "google_pay";
      }
      if (!detected) {
        setAvailableWallet(null);
        return;
      }
      pr.on("paymentmethod", onPaymentMethod);
      activePr = pr;
      if (!canceled) {
        setAvailableWallet(detected);
        setPaymentRequest(pr);
      }
    })();
    return () => {
      canceled = true;
      activePr?.off("paymentmethod", onPaymentMethod);
      setPaymentRequest(null);
      setAvailableWallet(null);
    };
  }, [
    stripe,
    postTipAmount?.data?.clientSecret,
    dispatch,
    finalizeTipAfterSucceededIntent,
  ]);
  // If wallet tab was selected but paymentRequest disappears, fall back to Card.
  useEffect(() => {
    if (walletTab === "wallet" && !paymentRequest) {
      setWalletTab("card");
    }
  }, [walletTab, paymentRequest]);
  useEffect(() => {
    setStripeLoading(true);
    const t = setTimeout(() => {
      setStripeLoading(false);
    }, 3000);
    return () => clearTimeout(t);
  }, []);
  // ✅ Wallet tab click handler — validates terms then opens the
  // device-native wallet sheet (Apple Pay or Google Pay) via
  // paymentRequest.show(). MUST be called inside the user-gesture click
  // handler so the browser allows the wallet sheet to open.
  const handleWalletTabClick = () => {
    if (!paymentRequest) {
      return;
    }
    // Reset previous errors
    setTermsError("");
    setError("");
    // Visually mark the wallet tab as selected
    setWalletTab("wallet");
    // Validate terms BEFORE opening the sheet
    if (!termsChecked) {
      setTermsError("You must agree to the terms and conditions");
      return;
    }
    try {
      // Programmatically open the wallet sheet — onPaymentMethod (set up in
      // the useEffect above) will receive the result with payerName + payerEmail.
      paymentRequest.show();
    } catch (e) {
      const walletName =
        availableWallet === "apple_pay" ? "Apple Pay" : "Google Pay";
      toast.error(`Could not open ${walletName}. Please try again.`);
    }
  };
  const handleCardTabClick = () => {
    setWalletTab("card");
  };
  // ─── Custom tab styles (matches the original Stripe tab look) ────────────
  const tabBaseStyle: React.CSSProperties = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "12px 8px",
    background: "#ffffff",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    transition: "border 0.15s ease",
    userSelect: "none",
    minHeight: 70,
  };
  const tabSelectedStyle: React.CSSProperties = {
    ...tabBaseStyle,
    border: "2px solid #1d4ed8",
    color: "#1d4ed8",
  };
  const tabUnselectedStyle: React.CSSProperties = {
    ...tabBaseStyle,
    border: "1px solid #e6e6e6",
    color: "#30313d",
  };
  // ─── Google Pay logo SVG (used inside the "G Pay" pill on the tab) ───────
  const googleGLogo = (
    <svg width="14" height="14" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.6 9.2c0-.6-.1-1.2-.2-1.7H9v3.3h4.8c-.2 1.1-.8 2-1.8 2.6v2.2h2.9c1.7-1.6 2.7-3.9 2.7-6.4z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.4 0 4.5-.8 6-2.2l-2.9-2.2c-.8.5-1.8.9-3.1.9-2.4 0-4.4-1.6-5.1-3.8H.9v2.3C2.4 15.9 5.5 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.9 10.7c-.2-.5-.3-1.1-.3-1.7s.1-1.2.3-1.7V5H.9C.3 6.2 0 7.6 0 9s.3 2.8.9 4l3-2.3z"
      />
      <path
        fill="#EA4335"
        d="M9 3.6c1.3 0 2.5.5 3.4 1.3L15 2.4C13.5.9 11.4 0 9 0 5.5 0 2.4 2.1.9 5l3 2.3C4.6 5.2 6.6 3.6 9 3.6z"
      />
    </svg>
  );
  // The small "G Pay" pill badge that sits on top of the Google Pay tab.
  const gPayPill = (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 12,
        border: "1px solid #dadce0",
        background: "#ffffff",
        fontSize: 11,
        fontWeight: 500,
        color: "#3c4043",
        lineHeight: 1.2,
      }}
    >
      {googleGLogo}
      Pay
    </span>
  );
  // ─── Apple Pay logo SVG ─────────────────────────────────────────────────
  const appleLogo = (
    <svg width="11" height="13" viewBox="0 0 17 21" aria-hidden="true">
      <path
        fill="#ffffff"
        d="M13.6 11.1c0-2.4 2-3.5 2.1-3.6-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.7.9-.8 0-1.9-.9-3.2-.8-1.6 0-3.2 1-4 2.4-1.7 3-.4 7.4 1.2 9.8.8 1.2 1.8 2.5 3.1 2.4 1.2 0 1.7-.8 3.1-.8 1.4 0 1.9.8 3.1.8 1.3 0 2.1-1.2 2.9-2.4.9-1.4 1.3-2.7 1.3-2.8-.1-.1-2.4-1-2.4-3.7zM11.2 4c.6-.8 1.1-1.9 1-3-.9 0-2.1.6-2.7 1.4-.6.7-1.1 1.8-1 2.9 1.1.1 2.1-.5 2.7-1.3z"
      />
    </svg>
  );
  // The black Apple Pay pill badge (used on iPhone Safari).
  const applePayPill = (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 10px",
        borderRadius: 12,
        border: "1px solid #000000",
        background: "#000000",
        color: "#ffffff",
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.2,
      }}
    >
      {appleLogo}
      Pay
    </span>
  );
  // What to render inside the wallet tab — chosen at runtime based on what
  // the device + Stripe Dashboard config actually exposed via canMakePayment.
  const walletPill =
    availableWallet === "apple_pay" ? applePayPill : gPayPill;
  const walletTabLabel =
    availableWallet === "apple_pay" ? "Apple Pay" : "Google Pay";
  return (
    <div className={styles.stripe_container}>
      <form>
        {stripeLoading ? (
          <span
            className="loader"
            style={{
              display: "flex",
              height: 40,
            }}
          >
            <RotatingLines
              visible={true}
              strokeColor="#000000"
              width={"25px"}
            />
          </span>
        ) : (
          <>
            {/* Custom payment-method tabs.
                - "Card"   tab → shows the card form below.
                - "Wallet" tab → directly opens Apple Pay (iOS) or
                  Google Pay (Chrome/Android) via paymentRequest.show().
                  No standalone wallet button is rendered; the tab IS the
                  trigger. The branding (pill + label) is chosen at runtime
                  from canMakePayment(). */}
            {paymentRequest && availableWallet && (
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={handleCardTabClick}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleCardTabClick();
                  }}
                  style={
                    walletTab === "card" ? tabSelectedStyle : tabUnselectedStyle
                  }
                >
                  <svg
                    width="22"
                    height="16"
                    viewBox="0 0 20 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="0.5"
                      width="19"
                      height="13"
                      rx="2"
                      stroke="currentColor"
                    />
                    <rect y="3" width="20" height="2" fill="currentColor" />
                  </svg>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>Card</span>
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={handleWalletTabClick}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ")
                      handleWalletTabClick();
                  }}
                  style={
                    walletTab === "wallet"
                      ? tabSelectedStyle
                      : tabUnselectedStyle
                  }
                >
                  {walletPill}
                  <span style={{ fontSize: 14, fontWeight: 500 }}>
                    {walletTabLabel}
                  </span>
                </div>
              </div>
            )}
            {/* Card form (PaymentElement) — visible only when Card tab active. */}
            <div style={{ display: walletTab === "card" ? "block" : "none" }}>
              <PaymentElement
                key={`${stripeLoading ? "loading" : "loaded"}-${shouldRequireManualCardholderName ? "manual-name" : "wallet-name-auto"}`}
                onChange={(event) => {
                  const paymentMethodType = event.value?.type || "";
                  setPaymentElementError("");
                  setCardholderNameError("");
                  setSelectedPaymentMethod(paymentMethodType);
                }}
                options={{
                  // Wallets handled by our custom Wallet tab → paymentRequest.show().
                  wallets: {
                    googlePay: "never",
                    applePay: "never",
                  },
                  fields: {
                    billingDetails: {
                      name: shouldRequireManualCardholderName ? "never" : "auto",
                      email: isEmailRequired ? "never" : "auto",
                      phone: "auto",
                      address: "auto",
                    },
                  },
                }}
                className={paymentElementError ? styles.paymentElementError : ""}
              />
              {shouldRequireManualCardholderName && (
                <div className={styles.stripe_wrapper}>
                  <Row>
                    <Col lg={12} className="mt-1">
                      <label htmlFor="cardholderName">Cardholder Name</label>
                      <input
                        type="text"
                        id="cardholderName"
                        value={cardholderName}
                        onChange={(e) => {
                          setCardholderNameError("");
                          setCardholderName(e.target.value);
                        }}
                        placeholder="Cardholder Name"
                        className={`${styles.email} ${cardholderNameError ? styles.errorField : ""}`}
                        style={{
                          marginBottom: 0,
                        }}
                        required
                        aria-invalid={!!cardholderNameError}
                      />
                      {cardholderNameError && (
                        <p
                          style={{
                            color: "#d22b2b",
                            fontSize: 12,
                            display: "flex",
                            width: "100%",
                          }}
                        >
                          {cardholderNameError}
                        </p>
                      )}
                    </Col>
                  </Row>
                </div>
              )}
            </div>
            {/* Hint shown when wallet tab is active (in case user closed the
                wallet sheet — they can re-tap the wallet tab to retry). */}
            {walletTab === "wallet" && (
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: 13,
                  color: "#5b6271",
                  textAlign: "center",
                }}
              >
                If the {walletTabLabel} popup didn't open, tap the{" "}
                {walletTabLabel} tab again.
              </p>
            )}
          </>
        )}
        {/* Email + Terms section. Email shown only on Card tab (the wallet
            collects it via the Apple Pay / Google Pay sheet). Terms always shown. */}
        <div className={styles.stripe_wrapper} style={{ marginBottom: 15 }}>
          <Row>
            <Col lg={12} className="mt-1">
              {!isWalletPayment && (
                <>
                  <label htmlFor="email">
                    Email address {isEmailRequired ? "" : <span>Optional</span>}
                  </label>
                  <input
                    type="email"
                    id="email"
                    onChange={(e) => {
                      setError("");
                      handleEmailChange(e);
                    }}
                    value={email}
                    placeholder="Enter email"
                    className={`${styles.email} ${error ? styles.errorField : ""}`}
                    aria-invalid={!!error}
                    style={{
                      marginBottom: 0,
                    }}
                  />
                  {error && (
                    <p
                      style={{
                        color: "#d22b2b",
                        fontSize: 12,
                        display: "flex",
                        width: "100%",
                      }}
                    >
                      {error}
                    </p>
                  )}
                </>
              )}
              <div
                className={styles.checkbox_container}
                style={{
                  marginTop: !isWalletPayment ? 10 : 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <input
                  type="checkbox"
                  id="termsCheckbox"
                  checked={termsChecked}
                  onChange={() => {
                    setTermsError("");
                    setTermsChecked(!termsChecked);
                  }}
                  className={`${styles.checkbox} ${termsError ? styles.errorCheckbox : ""}`}
                  aria-invalid={!!termsError}
                />
                <label
                  htmlFor="termsCheckbox"
                  style={{ marginLeft: 10, color: "black", fontSize: "14px" }}
                >
                  I agree with the{" "}
                  <a
                    href="https://toptipper.s3.us-east-1.amazonaws.com/static-assets/TopTipp-End-User-Terms-of-Service.pdf"
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <span
                      style={{
                        color: "blue",
                        fontSize: "14px",
                      }}
                    >
                      Terms and Conditions
                    </span>
                  </a>
                </label>
              </div>
              {termsError && (
                <p
                  style={{
                    color: "#d22b2b",
                    fontSize: 12,
                    display: "flex",
                    width: "100%",
                  }}
                >
                  {termsError}
                </p>
              )}
            </Col>
          </Row>
        </div>
        {/* "Send tip" button shown for BOTH Card and Wallet flows.
            - Card:   triggers handleSubmit (PaymentElement confirm).
            - Wallet: re-opens the Apple Pay / Google Pay sheet via
              handleWalletTabClick.
            The button shows the spinner whenever payment is in flight
            (loading || sendTipLoading) — this covers the wallet flow too,
            because the wallet onPaymentMethod handler sets `loading` true
            during the confirm + finalize cycle. */}
        <div className={styles.btn}>
          <ThemeButton
            disabled={loading || stripeLoading || sendTipLoading}
            onClick={(e) => {
              if (walletTab === "wallet") {
                e.preventDefault();
                handleWalletTabClick();
              } else {
                handleSubmit(e);
              }
            }}
            loading={loading || sendTipLoading}
          >
            Send
          </ThemeButton>
        </div>
      </form>
      <ConfirmationModal
        handleToggle={handleErrorModal}
        icon={oops}
        title={""}
        color={"#676D7A"}
        desc={"Your card has been declined."}
        show={errorModal}
      />{" "}
    </div>
  );
}
